// Pure per-format scoring for the /score scorecard. Every team enters ONE score
// per hole (bestBall) — the one-team-score-per-hole model — and each format
// derives its metric from that. Verified against GolfGamesScorecard + CC's
// ggFormats.js + TOURNAMENT_FORMATS.md. No React/Firebase — unit-testable.
import { teamGross, teamVsPar, teamThru, DEFAULT_PARS, rankTeams } from './scoring.js'
import { strokesOnHole } from './handicapCalc.js'

// ── Stableford (standard) ────────────────────────────────────────────────
// par=2, birdie=3, eagle=4, bogey=1, double+=0. Matches GGS + ggFormats.
export function stablefordHolePoints(strokes, par) {
  if (!Number.isFinite(strokes) || strokes <= 0) return 0
  return Math.max(0, 2 - (strokes - par))
}
export function teamStablefordPoints(scores, pars = DEFAULT_PARS) {
  let pts = 0
  for (let h = 1; h <= 18; h++) {
    const s = scores?.[h]?.bestBall
    if (Number.isFinite(s)) pts += stablefordHolePoints(s, pars[h - 1] || 4)
  }
  return pts
}

// ── Quota (owner standard — DOUBLING scale) ──────────────────────────────
// bogey=1, par=2, birdie=4, eagle+=8. (GGS "dogfight" uses a linear 1/2/3/4 —
// flagged; this uses the owner's researched 1/2/4/8.) Quota target = 36 − hcp.
export function quotaHolePoints(strokes, par) {
  if (!Number.isFinite(strokes) || strokes <= 0) return 0
  const d = strokes - par
  if (d <= -2) return 8
  if (d === -1) return 4
  if (d === 0) return 2
  if (d === 1) return 1
  return 0
}
export function teamQuotaPoints(scores, pars = DEFAULT_PARS) {
  let pts = 0
  for (let h = 1; h <= 18; h++) {
    const s = scores?.[h]?.bestBall
    if (Number.isFinite(s)) pts += quotaHolePoints(s, pars[h - 1] || 4)
  }
  return pts
}
// Team quota target: sum of players' (36 − hcp); demo teams may carry a
// pre-computed info.quota; else derive from a single team handicap.
export function teamQuotaTarget(team) {
  if (Number.isFinite(Number(team?.info?.quota))) return Number(team.info.quota)
  const hcps = team?.info?.playerHandicaps
  if (Array.isArray(hcps) && hcps.length) return hcps.reduce((q, h) => q + (36 - (Number(h) || 0)), 0)
  return 36 - (Number(team?.info?.handicap) || 0)
}

// ── Skins (low score/hole, ties carry + stack; carryover ON, validation OFF) ──
// Outright winner collects the carried skins. Matches ggFormats defaults.
// teams: [{ id, scores }]. Returns { [teamId]: skinsWon }.
export function computeSkinsByTeam(teams, pars = DEFAULT_PARS) {
  const out = Object.fromEntries(teams.map((t) => [t.id, 0]))
  let carried = 0
  for (let h = 1; h <= 18; h++) {
    let best = Infinity, bestTeam = null, tie = false, anyScored = false
    for (const t of teams) {
      const s = t.scores?.[h]?.bestBall
      if (!Number.isFinite(s)) continue
      anyScored = true
      if (s < best) { best = s; bestTeam = t.id; tie = false }
      else if (s === best) tie = true
    }
    if (!anyScored) continue
    const atStake = 1 + carried
    if (bestTeam && !tie) { out[bestTeam] += atStake; carried = 0 }
    else { carried = atStake } // tie → carry ON, stacks onto next hole
  }
  return out
}

// ── Modified Stableford (configurable points table) ───────────────────────
// Table maps score-vs-par (strokes − par) → points. Keys clamp: ≤-3 (albatross+),
// -2 (eagle), -1 (birdie), 0 (par), 1 (bogey), ≥2 (double+). The default mirrors
// the common GG Modified Stableford scale; a tournament can override via
// info.modStablefordTable. Standard Stableford stays its own fixed function.
export const DEFAULT_MOD_STABLEFORD = { albatross: 8, eagle: 5, birdie: 2, par: 0, bogey: -1, double: -3 }
export function modStablefordHolePoints(strokes, par, table = DEFAULT_MOD_STABLEFORD) {
  if (!Number.isFinite(strokes) || strokes <= 0) return 0
  const d = strokes - par
  if (d <= -3) return table.albatross
  if (d === -2) return table.eagle
  if (d === -1) return table.birdie
  if (d === 0) return table.par
  if (d === 1) return table.bogey
  return table.double
}
export function teamModifiedStablefordPoints(scores, pars = DEFAULT_PARS, table = DEFAULT_MOD_STABLEFORD) {
  let pts = 0
  for (let h = 1; h <= 18; h++) {
    const s = scores?.[h]?.bestBall
    if (Number.isFinite(s)) pts += modStablefordHolePoints(s, pars[h - 1] || 4, table)
  }
  return pts
}

// ── Par / Bogey (match-vs-par) ─────────────────────────────────────────────
// Each hole is a match against par using the NET score: net < par → +1 (win),
// net === par → 0 (halve), net > par → -1 (loss). Total is the "up on par"
// figure; highest wins. Net per hole uses the already-shared strokesOnHole.
export function parBogeyHolePoints(net, par) {
  if (!Number.isFinite(net) || net <= 0) return 0
  if (net < par) return 1
  if (net > par) return -1
  return 0
}

// ── Team-counting value from individual balls (Four-Ball family) ──────────
// `balls` = per-player gross for ONE hole, e.g. [p1,p2,p3,p4] (any length 1–4;
// null/0 entries are ignored). `mode` picks how many balls count:
//   best1            lowest ball              (classic best ball)
//   best2            sum of the two lowest     (also 2-player aggregate)
//   best3            sum of the three lowest
//   aggregate        sum of ALL balls
//   bestN            sum of the N lowest       (opts.n)
//   1234 / 1-2-3-4   by hole: hole 1→1 ball, 2→2, 3→3, 4→4, repeating (opts.holeIndex, 0-based)
// Returns the hole's counting score (written to bestBall), or null if nothing
// is entered yet. Pure — the scorecard calls this to derive bestBall on save.
export function countingScore(balls, mode = 'best1', opts = {}) {
  const vals = (balls || []).map(Number).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b)
  if (vals.length === 0) return null
  const sumN = (n) => vals.slice(0, Math.max(1, Math.min(n, vals.length))).reduce((a, b) => a + b, 0)
  switch (mode) {
    case 'best1': return vals[0]
    case 'best2': return sumN(2)
    case 'best3': return sumN(3)
    case 'aggregate': return vals.reduce((a, b) => a + b, 0)
    case 'bestN': return sumN(Number(opts.n) || vals.length)
    case '1234':
    case '1-2-3-4': {
      const count = ((Number(opts.holeIndex) || 0) % 4) + 1
      return sumN(count)
    }
    default: return vals[0]
  }
}

// Four-Ball sub-mode: explicit info.fourBallMode wins, else derived from the
// format string, else best1 (classic best ball = single counting score).
export function fourBallMode(info) {
  if (info?.fourBallMode) return info.fourBallMode
  switch (info?.format) {
    case 'Four-Ball Best 2': return 'best2'
    case 'Four-Ball Best 3': return 'best3'
    case 'Four-Ball 1-2-3-4': return '1234'
    case 'Aggregate': return 'aggregate'
    default: return 'best1'
  }
}

// ── Derived-handicap formats (compute a handicap from the round, then net) ──
// All operate on an 18-entry per-hole gross array (the player's bestBall card).
function grossArray(scores) {
  const a = []
  for (let h = 1; h <= 18; h++) a.push(Number(scores?.[h]?.bestBall) || 0)
  return a
}

// System 36: per hole, par-or-better = 2 pts, bogey = 1 pt, worse = 0.
// Course handicap = 36 − points. (Then net = gross − handicap.)
export function system36Handicap(scores, pars = DEFAULT_PARS) {
  const g = grossArray(scores)
  let pts = 0
  for (let i = 0; i < 18; i++) {
    if (!g[i]) continue
    const d = g[i] - (pars[i] || 4)
    if (d <= 0) pts += 2
    else if (d === 1) pts += 1
  }
  return Math.max(0, 36 - pts)
}

// Callaway: deduct the worst N hole scores (N from the gross-score table), then
// apply the units-digit adjustment. Holes 17 & 18 are never used as "worst"
// (standard Callaway). Net = gross − handicap.
const CALLAWAY_WORST = [ // [maxGross, numWorstHoles] — ½ represented as .5
  [75, 0], [80, 0.5], [85, 1], [90, 1.5], [95, 2], [100, 2.5],
  [105, 3], [110, 3.5], [115, 4], [120, 4.5], [125, 5], [130, 5.5], [Infinity, 6],
]
const CALLAWAY_ADJ = [-2, -1, 0, 1, 2, 2, 1, 0, -1, -2] // by units digit of gross 0..9
export function callawayHandicap(scores, pars = DEFAULT_PARS) {
  const g = grossArray(scores).slice(0, 16).filter((n) => n > 0) // exclude holes 17-18
  const gross = grossArray(scores).reduce((a, b) => a + b, 0)
  if (gross === 0) return 0
  const row = CALLAWAY_WORST.find(([max]) => gross <= max) || [Infinity, 6]
  const n = row[1]
  const worst = [...g].sort((a, b) => b - a)
  let deduct = 0
  const whole = Math.floor(n)
  for (let i = 0; i < whole; i++) deduct += worst[i] || 0
  if (n - whole === 0.5) deduct += (worst[whole] || 0) / 2
  const adj = CALLAWAY_ADJ[gross % 10] || 0
  return Math.max(0, Math.round(deduct + adj))
}

// Peoria: 6 hidden holes × 3 = estimated 18-hole gross; handicap =
// (estimate − course par) × 0.8. Modified Peoria uses 12 holes × 1.5.
export const DEFAULT_PEORIA_HOLES = [2, 5, 7, 11, 14, 16]            // 1-based
export const DEFAULT_MOD_PEORIA_HOLES = [1, 3, 4, 6, 7, 9, 10, 12, 13, 15, 16, 18]
function peoriaFromHoles(scores, pars, holes, multiplier) {
  const g = grossArray(scores)
  const coursePar = pars.reduce((a, b) => a + (b || 4), 0)
  let sum = 0
  for (const h of holes) sum += g[h - 1] || 0
  const estimate = sum * multiplier
  return Math.max(0, Math.round((estimate - coursePar) * 0.8))
}
export function peoriaHandicap(scores, pars = DEFAULT_PARS, holes = DEFAULT_PEORIA_HOLES) {
  return peoriaFromHoles(scores, pars, holes, 3)
}
export function modifiedPeoriaHandicap(scores, pars = DEFAULT_PARS, holes = DEFAULT_MOD_PEORIA_HOLES) {
  return peoriaFromHoles(scores, pars, holes, 1.5)
}

// ── Format → metric (how the standing ranks + what the summary shows) ────
// 'strokes'  low total wins  (Best Ball, Shamble, Alternate Shot, Scramble,
//            Stroke Individual, Four-Ball best-N, Aggregate, Chapman, Greensome…)
// 'stableford' / 'modified-stableford' / 'quota' / 'parbogey'  high points win
// 'skins'    most skins wins
// 'system36' / 'callaway' / 'peoria' / 'modified-peoria'  low NET total wins
export function formatMetric(format) {
  if (format === 'Stableford') return 'stableford'
  if (format === 'Modified Stableford') return 'modified-stableford'
  if (format === 'Quota') return 'quota'
  if (format === 'Skins') return 'skins'
  if (format === 'Par/Bogey' || format === 'Par-Bogey') return 'parbogey'
  if (format === 'System 36') return 'system36'
  if (format === 'Callaway') return 'callaway'
  if (format === 'Peoria') return 'peoria'
  if (format === 'Modified Peoria') return 'modified-peoria'
  return 'strokes'
}
// Higher metric value = better? Points-style metrics yes; strokes and the
// derived-handicap NET metrics are low-wins.
const HIGHER_WINS_METRICS = new Set(['stableford', 'modified-stableford', 'quota', 'skins', 'parbogey'])
export const higherWins = (metric) => HIGHER_WINS_METRICS.has(metric)

// One team's metric value (skins handled separately since it needs the field).
// `opts`: { strokeIndex, modStablefordTable, peoriaHoles, modPeoriaHoles } — all
// optional; defaults keep every existing caller (teamMetricValue(metric, team))
// working unchanged.
export function teamMetricValue(metric, team, pars = DEFAULT_PARS, opts = {}) {
  const scores = team?.scores || {}
  const hcp = Number(team?.info?.handicap) || 0
  switch (metric) {
    case 'stableford': return teamStablefordPoints(scores, pars)
    case 'modified-stableford': return teamModifiedStablefordPoints(scores, pars, opts.modStablefordTable)
    case 'quota': return teamQuotaPoints(scores, pars) - teamQuotaTarget(team)
    case 'parbogey': {
      // Net per hole = bestBall − strokes received (single-handicap individual).
      const si = opts.strokeIndex
      let pts = 0
      for (let h = 1; h <= 18; h++) {
        const s = scores?.[h]?.bestBall
        if (!Number.isFinite(s)) continue
        const recv = si ? strokesOnHole(hcp, h - 1, si) : 0
        pts += parBogeyHolePoints(s - recv, pars[h - 1] || 4)
      }
      return pts
    }
    case 'system36': return teamGross(scores) - system36Handicap(scores, pars)
    case 'callaway': return teamGross(scores) - callawayHandicap(scores, pars)
    case 'peoria': return teamGross(scores) - peoriaHandicap(scores, pars, opts.peoriaHoles)
    case 'modified-peoria': return teamGross(scores) - modifiedPeoriaHandicap(scores, pars, opts.modPeoriaHoles)
    default: return teamGross(scores) // strokes
  }
}

// ── Metric-aware standings ranker ──────────────────────────────────────────
// ONE entry point for the leaderboard + TV. For 'strokes' (and net) it defers
// to the existing rankTeams so stroke/net/vs-par events render byte-identical
// (PBCC unaffected). For every other metric it ranks by teamMetricValue with the
// correct direction (higherWins), using the same unstarted-last + hole-countback
// tie-break shape as rankTeams. Skins ranks by skins won (needs the whole field).
export function rankByFormat(teams, format, pars = DEFAULT_PARS, opts = {}) {
  const { useNet = false, strokeIndex } = opts
  const metric = formatMetric(format)
  if (metric === 'strokes') return rankTeams(teams, useNet, pars)

  if (metric === 'skins') {
    const won = computeSkinsByTeam(teams, pars)
    const rows = teams.map((t) => ({ ...t, thru: teamThru(t.scores), metric, metricValue: won[t.id] || 0, skins: won[t.id] || 0 }))
    rows.sort((a, b) => (b.metricValue - a.metricValue) || (b.thru - a.thru) || (a.info?.name || '').localeCompare(b.info?.name || ''))
    return rows
  }

  const hi = higherWins(metric)
  const rows = teams.map((t) => ({
    ...t,
    thru: teamThru(t.scores),
    gross: teamGross(t.scores),
    vsPar: teamVsPar(t.scores, pars),
    metric,
    metricValue: teamMetricValue(metric, t, pars, { strokeIndex, modStablefordTable: opts.modStablefordTable, peoriaHoles: opts.peoriaHoles, modPeoriaHoles: opts.modPeoriaHoles }),
  }))
  rows.sort((a, b) => {
    const aStarted = a.thru > 0
    const bStarted = b.thru > 0
    if (aStarted !== bStarted) return aStarted ? -1 : 1
    if (a.metricValue !== b.metricValue) return hi ? b.metricValue - a.metricValue : a.metricValue - b.metricValue
    if (a.thru !== b.thru) return b.thru - a.thru
    for (let h = 18; h >= 1; h--) {
      const ah = a.scores?.[h]?.bestBall
      const bh = b.scores?.[h]?.bestBall
      if (Number.isFinite(ah) && Number.isFinite(bh) && ah !== bh) return ah - bh
    }
    return (a.info?.name || '').localeCompare(b.info?.name || '')
  })
  return rows
}

// Short label for the metric's primary column on the board (points / net / skins).
export function metricColumnLabel(metric) {
  if (metric === 'stableford' || metric === 'modified-stableford' || metric === 'quota' || metric === 'parbogey') return 'PTS'
  if (metric === 'skins') return 'SKINS'
  if (metric === 'system36' || metric === 'callaway' || metric === 'peoria' || metric === 'modified-peoria') return 'NET'
  return 'TOT'
}
